import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, User, Moon, Sun, Monitor, UserCog } from 'lucide-react';
import { useDashboardStore } from '@/stores/dashboardStore';
import { useUIStore } from '@/stores/uiStore';
import { useAdminStore } from '@/stores/adminStore';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import prmLogo from '@/assets/prm-claro.png';

export default function TopBar() {
  const navigate = useNavigate();
  const { logout, displayName, user } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const setSettingsOpen = useUIStore((s) => s.setSettingsOpen);
  const setAdminOpen = useAdminStore((s) => s.setOpen);
  const isAdmin = user?.role === 'admin';

  const totalActive = useDashboardStore((s) => s.total_active);
  const inReaction = useDashboardStore((s) => s.in_reaction);
  const enRoute = useDashboardStore((s) => s.en_route);
  const avgSpeed = useDashboardStore((s) => s.avg_speed);
  const isLoaded = useDashboardStore((s) => s.isLoaded);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const statValue = (val: number | undefined) =>
    isLoaded && val != null ? val : '...';

  return (
    <header className="fixed top-0 left-0 right-0 h-14 bg-bg-secondary border-b border-border-strong flex items-center justify-between px-5 z-[1000] shadow-lg">
      {/* Logo */}
      <div className="flex items-center gap-3">
        <img
          src={prmLogo}
          alt="PR Mob Predictive Mobility"
          className="h-9 w-auto max-w-[116px] rounded-md object-contain shadow-md"
        />
      </div>

      {/* Stats - only 4 key metrics, rest on floating map cards */}
      <div className="hidden md:flex items-center gap-6 flex-1 justify-center">
        <StatItem label="Activos:" value={statValue(totalActive)} dotColor="green" />
        <StatItem label="Reacción:" value={statValue(inReaction)} dotColor="red" />
        <StatItem label="En ruta:" value={statValue(enRoute)} dotColor="blue" />
        <StatItem label="Vel. Prom:" value={avgSpeed != null && isLoaded ? `${avgSpeed}` : '...'} suffix=" km/h" />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {isAdmin && (
          <>
            <button
              onClick={() => setAdminOpen(true)}
              className="w-9 h-9 bg-bg-tertiary border border-border-subtle rounded-lg text-text-muted hover:border-accent-purple hover:text-accent-purple flex items-center justify-center transition-all"
              title="Monitor"
            >
              <Monitor size={16} />
            </button>
            <button
              onClick={() => navigate('/admin/users')}
              className="w-9 h-9 bg-bg-tertiary border border-border-subtle rounded-lg text-text-muted hover:border-accent-purple hover:text-accent-purple flex items-center justify-center transition-all"
              title="Usuarios"
            >
              <UserCog size={16} />
            </button>
          </>
        )}

        <button
          onClick={toggleTheme}
          className="w-9 h-9 bg-bg-tertiary border border-border-subtle rounded-lg text-text-muted hover:border-accent-blue hover:text-accent-blue flex items-center justify-center transition-all"
          title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
        >
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        </button>

        <button
          onClick={() => setSettingsOpen(true)}
          className="w-9 h-9 bg-bg-tertiary border border-border-subtle rounded-lg text-text-muted hover:border-accent-blue hover:text-accent-blue flex items-center justify-center transition-all"
          title="Configuración"
        >
          <Settings size={16} />
        </button>

        {/* User menu */}
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="w-9 h-9 bg-bg-tertiary border border-border-subtle rounded-lg text-text-muted hover:border-accent-blue hover:text-accent-blue flex items-center justify-center transition-all"
          >
            <User size={16} />
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 top-11 bg-bg-elevated border border-border-subtle rounded-lg shadow-xl min-w-[180px] py-1 z-50 animate-fade-in">
              <div className="px-3 py-2 border-b border-border-subtle">
                <div className="text-sm font-medium text-text-primary">
                  {displayName}
                </div>
                <div className="text-xs text-text-muted">{user?.role || 'Usuario'}</div>
              </div>
              <button
                onClick={logout}
                className="w-full px-3 py-2 text-sm text-accent-red hover:bg-bg-tertiary flex items-center gap-2 transition-colors"
              >
                <LogOut size={14} />
                Cerrar Sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function StatItem({
  label,
  value,
  dotColor,
  suffix = '',
}: {
  label: string;
  value: string | number;
  dotColor?: string;
  suffix?: string;
}) {
  const dotClasses: Record<string, string> = {
    green: 'bg-accent-green shadow-[0_0_8px_var(--accent-green)]',
    red: 'bg-accent-red shadow-[0_0_8px_var(--accent-red)]',
    blue: 'bg-accent-blue shadow-[0_0_8px_var(--accent-blue)]',
  };

  return (
    <div className="flex items-center gap-2 text-[13px]">
      {dotColor && (
        <div
          className={`w-2 h-2 rounded-full animate-pulse-dot ${dotClasses[dotColor] || ''}`}
        />
      )}
      <span className="text-text-muted text-xs">{label}</span>
      <span className={`font-bold font-mono ${value === '...' ? 'text-text-muted' : 'text-text-primary'}`}>
        {value}{suffix}
      </span>
    </div>
  );
}
